import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Modal,
  ModalFooter,
  Button,
  Icon,
} from '@folio/stripes/components';

const RecordLockedWarningModal = ({
  isOpen,
  recordType,
  lockedBy,
  duration,
  onCancel,
  onForceEdit,
  allowForceEdit = true,
}) => {
  const getDurationText = (minutes) => {
    if (minutes < 60) {
      return (
        <FormattedMessage
          id="ui-plugin-inventory-templates.recordLock.duration.minutes"
          defaultMessage="{minutes} {minutes, plural, one {دقيقة} other {دقيقة}}"
          values={{ minutes }}
        />
      );
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (remainingMinutes === 0) {
      return (
        <FormattedMessage
          id="ui-plugin-inventory-templates.recordLock.duration.hours"
          defaultMessage="{hours} {hours, plural, one {ساعة} other {ساعة}}"
          values={{ hours }}
        />
      );
    }

    return (
      <FormattedMessage
        id="ui-plugin-inventory-templates.recordLock.duration.hoursAndMinutes"
        defaultMessage="{hours} {hours, plural, one {ساعة} other {ساعة}} و {minutes} {minutes, plural, one {دقيقة} other {دقيقة}}"
        values={{ hours, minutes: remainingMinutes }}
      />
    );
  };

  const getRecordTypeText = (type) => {
    const typeMap = {
      instance: 'التسجيلة',
      holding: 'المقتنى',
      item: 'المادة'
    };
    return typeMap[type] || 'التسجيلة';
  };

  const modalLabel = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Icon icon="lock" size="medium" iconStyle="error" />
      <FormattedMessage
        id="ui-plugin-inventory-templates.recordLock.modal.title"
        defaultMessage="تحذير - التسجيلة محجوزة"
      />
    </div>
  );

  return (
    <Modal
      open={isOpen}
      onClose={onCancel}
      size="small"
      label={modalLabel}
      footer={
        <ModalFooter>
          <Button
            buttonStyle="primary"
            onClick={onCancel}
          >
            <FormattedMessage
              id="ui-plugin-inventory-templates.recordLock.button.goBack"
              defaultMessage="العودة"
            />
          </Button>

          {allowForceEdit && (
            <Button
              buttonStyle="danger"
              onClick={onForceEdit}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.recordLock.button.forceEdit"
                defaultMessage="المتابعة على مسؤوليتي"
              />
            </Button>
          )}
        </ModalFooter>
      }
    >
      <div style={{ padding: '20px 0' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeeba',
            borderRadius: '4px'
          }}
        >
          <Icon icon="exclamation-triangle" size="large" iconStyle="warning" />
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.recordLock.warning.title"
                defaultMessage="هذه {recordType} قيد التحرير حالياً"
                values={{ recordType: getRecordTypeText(recordType) }}
              />
            </div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.recordLock.warning.details"
                defaultMessage="محجوزة بواسطة: {lockedBy} منذ {duration}"
                values={{
                  lockedBy: <strong>{lockedBy}</strong>,
                  duration: <strong>{getDurationText(duration)}</strong>
                }}
              />
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.recordLock.explanation"
            defaultMessage="المستخدم الآخر قد يكون يعمل على تعديل هذه التسجيلة الآن. إذا تابعت التحرير، قد تفقد تغييراته أو تواجه مشاكل في الحفظ."
          />
        </div>

        {allowForceEdit && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <Icon icon="exclamation-circle" style={{ marginRight: '6px' }} />
            <FormattedMessage
              id="ui-plugin-inventory-templates.recordLock.forceEdit.warning"
              defaultMessage="إذا اخترت المتابعة، ستكون مسؤولاً عن تجنب التضارب مع المستخدم الآخر."
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

RecordLockedWarningModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  recordType: PropTypes.oneOf(['instance', 'holding', 'item']),
  lockedBy: PropTypes.string.isRequired,
  duration: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onForceEdit: PropTypes.func,
  allowForceEdit: PropTypes.bool,
};

RecordLockedWarningModal.defaultProps = {
  recordType: 'instance',
  onForceEdit: () => {},
  allowForceEdit: true,
};

export default RecordLockedWarningModal;
